/**
 * BusinessCartera — la pestaña "Cartera" del dashboard Business (COBRAR).
 * Quien me debe, plazo otorgado, dias de retraso, funnel de links (creados vs pagados),
 * aging, top deudores, DSO y flujo esperado. Crear/anular cobros = UN TOQUE (no mueve
 * dinero). El recordatorio se COPIA y lo envia el dueño por su WhatsApp.
 * Visual: .cartera-ola + tokens --fx-* (glass Ola A, claro + oscuro). Solo chrome.
 */
import { useEffect, useState } from 'react';
import { getCarteraBusiness, crearCobroBusiness, anularCobroBusiness } from '../../lib/cerebro';

const cop = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-CO');
const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }) : '—');

function copiar(texto, setMsg) {
  navigator.clipboard?.writeText(texto).then(() => {
    setMsg('Copiado ✓');
    setTimeout(() => setMsg(null), 2000);
  }).catch(() => setMsg('No pude copiar'));
}

function recordatorio(c) {
  const mora = c.diasVencido > 0 ? ` que vencio el ${fecha(c.vence)} (lleva ${c.diasVencido} dia${c.diasVencido === 1 ? '' : 's'} de retraso)` : ` que vence el ${fecha(c.vence)}`;
  return `Hola ${c.cliente}, ¿como estas? Te escribo para recordarte el pago de "${c.concepto}" por ${cop(c.montoCop)}${mora}. Puedes pagarlo facil y seguro con PSE en este link: ${c.link} — ¡Gracias!`;
}

const Metrica = ({ label, valor, sub, tono }) => (
  <div className={`cartera-ola__metric${tono === 'rojo' ? ' cartera-ola__metric--bad' : tono === 'verde' ? ' cartera-ola__metric--good' : ''}`}>
    <div className="cartera-ola__metric-lbl">{label}</div>
    <div className="cartera-ola__metric-val">{valor}</div>
    {sub && <div className="cartera-ola__metric-sub">{sub}</div>}
  </div>
);

export default function BusinessCartera() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  // Nuevo cobro (form inline)
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ clienteNombre: '', montoCop: '', concepto: '', factura: '', vence: '' });
  const [creando, setCreando] = useState(false);
  const [creado, setCreado] = useState(null); // resultado con link

  const cargar = () => {
    setLoading(true);
    getCarteraBusiness()
      .then((d) => { setData(d); setError(d?.sinEmpresa ? 'Tu usuario no tiene empresa vinculada.' : null); })
      .catch((e) => setError(e?.response?.status === 403 ? 'FlowexPay Business esta en piloto privado.' : 'No se pudo cargar tu cartera.'))
      .finally(() => setLoading(false));
  };
  useEffect(cargar, []);

  const crear = async (e) => {
    e.preventDefault();
    setCreando(true); setCreado(null);
    try {
      const r = await crearCobroBusiness({
        clienteNombre: form.clienteNombre, montoCop: Number(form.montoCop),
        concepto: form.concepto, factura: form.factura, vence: form.vence || undefined,
      });
      if (r?.ok) { setCreado(r.cobro); setForm({ clienteNombre: '', montoCop: '', concepto: '', factura: '', vence: '' }); cargar(); }
      else setMsg(r?.mensaje || 'No se pudo crear el cobro.');
    } catch { setMsg('No se pudo crear el cobro.'); }
    finally { setCreando(false); }
  };

  const anular = async (c) => {
    if (!window.confirm(`¿Anular el cobro a ${c.cliente} por ${cop(c.montoCop)}? El link quedara inactivo.`)) return;
    const r = await anularCobroBusiness(c.linkId).catch(() => null);
    setMsg(r?.mensaje || 'No se pudo anular.');
    cargar();
  };

  if (loading) return <div className="cartera-ola"><div className="cartera-ola__loading">Cargando tu cartera...</div></div>;
  if (error) return <div className="cartera-ola"><div className="cartera-ola__err">{error}</div></div>;

  const s = data?.stats || {};
  const aging = data?.aging || {};
  const totalAging = (aging.corriente || 0) + (aging.d1_30 || 0) + (aging.d31_60 || 0) + (aging.d60mas || 0);
  const pct = (v) => (totalAging > 0 ? Math.max(2, Math.round((v / totalAging) * 100)) : 0);

  const Tabla = ({ items, conMora }) => (
    <div className="cartera-ola__scroll">
      <table className="cartera-ola__table">
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Concepto</th>
            <th className="right">Monto</th>
            <th className="right">Plazo otorgado</th>
            <th>Vence</th>
            {conMora && <th className="right">Retraso</th>}
            <th className="right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.linkId}>
              <td className="cartera-ola__td-name">{c.cliente}</td>
              <td>{c.concepto}</td>
              <td className="right cartera-ola__td-amt">{cop(c.montoCop)}</td>
              <td className="right">{c.plazoDias != null ? `${c.plazoDias} dias` : '—'}</td>
              <td>{fecha(c.vence)}</td>
              {conMora && (
                <td className="right">
                  <span className="cartera-ola__chip cartera-ola__chip--bad">{c.diasVencido} dias</span>
                </td>
              )}
              <td className="cartera-ola__actions-cell">
                <a href={`https://wa.me/?text=${encodeURIComponent(recordatorio(c))}`} target="_blank" rel="noopener noreferrer" className="cartera-ola__text-btn cartera-ola__text-btn--good">WhatsApp</a>
                <button type="button" onClick={() => copiar(c.link, setMsg)} className="cartera-ola__text-btn">Copiar link</button>
                <button type="button" onClick={() => copiar(recordatorio(c), setMsg)} className="cartera-ola__text-btn cartera-ola__text-btn--warn">Copiar recordatorio</button>
                <button type="button" onClick={() => anular(c)} className="cartera-ola__text-btn cartera-ola__text-btn--danger">Anular</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={conMora ? 7 : 6} className="cartera-ola__td-empty">Nada por aqui.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="cartera-ola">
      <header className="cartera-ola__head">
        <div>
          <p className="cartera-ola__eyebrow">Cobrar</p>
          <h1 className="cartera-ola__title">Cartera</h1>
          <p className="cartera-ola__sub">Quien te debe, cuanto y hace cuanto. Crea el link, compartelo por WhatsApp, y tu cliente paga con PSE sin necesitar FlowexPay.</p>
        </div>
        <button type="button" onClick={() => { setFormOpen(!formOpen); setCreado(null); }} className="cartera-ola__btn cartera-ola__btn--primary cartera-ola__btn--sm">
          {formOpen ? 'Cerrar' : '+ Nuevo cobro'}
        </button>
      </header>

      {msg && <p className="cartera-ola__msg">{msg}</p>}

      {/* Nuevo cobro */}
      {formOpen && (
        <section className="cartera-ola__panel">
          <h2 className="cartera-ola__h2">Nuevo cobro</h2>
          <form onSubmit={crear} className="cartera-ola__form">
            <input required value={form.clienteNombre} onChange={(e) => setForm({ ...form, clienteNombre: e.target.value })} placeholder="Cliente (ej: Acme SAS)" className="cartera-ola__input" />
            <input required type="number" min="10000" max="10000000" value={form.montoCop} onChange={(e) => setForm({ ...form, montoCop: e.target.value })} placeholder="Monto COP" className="cartera-ola__input" />
            <input required value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder="Concepto (ej: Factura 4512)" className="cartera-ola__input" />
            <input value={form.vence} onChange={(e) => setForm({ ...form, vence: e.target.value })} type="date" className="cartera-ola__input" />
            <button type="submit" disabled={creando} className="cartera-ola__btn cartera-ola__btn--primary">{creando ? 'Creando...' : 'Crear cobro'}</button>
          </form>
          <p className="cartera-ola__hint">Un toque, sin codigo: crear un cobro no mueve dinero. Costo del recaudo visible: 0,5% (min $3.900, max $14.900) + IVA — hasta 10 veces mas barato que las pasarelas en facturas grandes.</p>
          {creado && (
            <div className="cartera-ola__alert cartera-ola__alert--ok cartera-ola__alert--mt">
              <p className="cartera-ola__alert-title">Cobro creado: {creado.cliente} · {cop(creado.montoCop)}</p>
              <p className="cartera-ola__alert-body">Costo del recaudo: {creado.costoRecaudo} · Te acreditamos: <strong>{creado.teAcreditamos}</strong></p>
              <div className="cartera-ola__alert-actions">
                <code className="cartera-ola__code">{creado.link}</code>
                <button type="button" onClick={() => copiar(creado.link, setMsg)} className="cartera-ola__text-btn">Copiar link</button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Metricas */}
      <div className="cartera-ola__metrics">
        <Metrica label="Por cobrar" valor={cop(data.totalPorCobrarCop)} sub={`${s.pendientes || 0} cobro(s) activo(s)`} />
        <Metrica label="Vencido" valor={cop(data.totalVencidoCop)} sub={`${(data.vencidos || []).length} cobro(s) en mora`} tono={data.totalVencidoCop > 0 ? 'rojo' : undefined} />
        <Metrica label="Entran esta semana" valor={cop(data.esperadoSemanaCop)} sub="si te pagan a tiempo" tono={data.esperadoSemanaCop > 0 ? 'verde' : undefined} />
        <Metrica label="Te pagan en" valor={data.dsoDias != null ? `${data.dsoDias} dias` : '—'} sub="promedio (DSO)" />
        <Metrica label="Tasa de cobro" valor={s.tasaCobroPct != null ? `${s.tasaCobroPct}%` : '—'} sub={`${s.pagados || 0} pagado(s) de ${s.creados || 0} link(s)`} />
      </div>

      {/* Aging */}
      {totalAging > 0 && (
        <section className="cartera-ola__panel">
          <h2 className="cartera-ola__h2">Edad de tu cartera</h2>
          <div className="cartera-ola__bar">
            {aging.corriente > 0 && <div className="cartera-ola__bar-seg" style={{ width: `${pct(aging.corriente)}%`, background: 'var(--ct-age-ok)' }} />}
            {aging.d1_30 > 0 && <div className="cartera-ola__bar-seg" style={{ width: `${pct(aging.d1_30)}%`, background: 'var(--ct-age-1)' }} />}
            {aging.d31_60 > 0 && <div className="cartera-ola__bar-seg" style={{ width: `${pct(aging.d31_60)}%`, background: 'var(--ct-age-2)' }} />}
            {aging.d60mas > 0 && <div className="cartera-ola__bar-seg" style={{ width: `${pct(aging.d60mas)}%`, background: 'var(--ct-age-3)' }} />}
          </div>
          <div className="cartera-ola__legend">
            <span><i className="cartera-ola__dot" style={{ background: 'var(--ct-age-ok)' }} />Al dia: <b>{cop(aging.corriente)}</b></span>
            <span><i className="cartera-ola__dot" style={{ background: 'var(--ct-age-1)' }} />1-30 dias: <b>{cop(aging.d1_30)}</b></span>
            <span><i className="cartera-ola__dot" style={{ background: 'var(--ct-age-2)' }} />31-60: <b>{cop(aging.d31_60)}</b></span>
            <span><i className="cartera-ola__dot" style={{ background: 'var(--ct-age-3)' }} />+60: <b>{cop(aging.d60mas)}</b></span>
          </div>
        </section>
      )}

      {/* Vencidos */}
      {(data.vencidos || []).length > 0 && (
        <section className="cartera-ola__panel cartera-ola__panel--bad">
          <h2 className="cartera-ola__h2 cartera-ola__h2--bad">En mora — mandales el recordatorio</h2>
          <Tabla items={data.vencidos} conMora />
        </section>
      )}

      {/* Pendientes */}
      <section className="cartera-ola__panel">
        <h2 className="cartera-ola__h2">Por vencer</h2>
        <Tabla items={data.pendientes || []} conMora={false} />
      </section>

      <div className="cartera-ola__grid-2">
        {/* Top deudores */}
        <section className="cartera-ola__panel">
          <h2 className="cartera-ola__h2">Quien concentra tu cartera</h2>
          {(data.topDeudores || []).length === 0 ? <div className="cartera-ola__empty">Sin deudores activos.</div> :
            (data.topDeudores || []).map((d) => (
              <div key={d.cliente} className="cartera-ola__row">
                <span className="cartera-ola__row-name">{d.cliente}</span>
                <span className="cartera-ola__row-amt">{cop(d.montoCop)} <span className="cartera-ola__row-pct">({d.pctCartera}%)</span></span>
              </div>
            ))}
          {(data.topDeudores || [])[0]?.pctCartera >= 50 && (
            <div className="cartera-ola__alert cartera-ola__alert--warn cartera-ola__alert--mt">
              Ojo: un solo cliente es mas de la mitad de tu cartera — si se atrasa, se atrasa tu caja.
            </div>
          )}
        </section>
        {/* Pagados recientes */}
        <section className="cartera-ola__panel">
          <h2 className="cartera-ola__h2">Pagados recientes</h2>
          {(data.pagadosRecientes || []).length === 0 ? <div className="cartera-ola__empty">Aun no hay pagos por link. Cuando tu cliente pague, lo veras aqui.</div> :
            (data.pagadosRecientes || []).map((p, i) => (
              <div key={i} className="cartera-ola__row">
                <span className="cartera-ola__row-name">{p.cliente}<span className="cartera-ola__row-meta">{p.concepto}</span></span>
                <span className="cartera-ola__row-amt cartera-ola__row-amt--good">{cop(p.montoCop)}</span>
              </div>
            ))}
        </section>
      </div>

      {/* Historial completo — MISMO lenguaje visual del historial de personas
          (tabla Fecha/Hora/Movimiento/Monto/Estado con chips de punto) */}
      <section className="cartera-ola__panel">
        <h2 className="cartera-ola__h2">Historial</h2>
        <div className="cartera-ola__scroll">
          <table className="cartera-ola__hist">
            <thead>
              <tr>
                {['Fecha', 'Hora', 'Movimiento', 'Monto', 'Estado'].map((name) => (
                  <th key={name}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.historial || []).map((h) => {
                const iso = h.creado ? new Date(h.creado).toISOString() : '';
                const chip = h.estado === 'pagado'
                  ? { txt: 'Pagado', cls: 'cartera-ola__chip--good' }
                  : h.estado === 'vencido'
                  ? { txt: 'Vencido', cls: 'cartera-ola__chip--bad' }
                  : h.estado === 'anulado'
                  ? { txt: 'Anulado', cls: 'cartera-ola__chip--muted' }
                  : { txt: 'Pendiente', cls: 'cartera-ola__chip--warn' };
                return (
                  <tr key={h.linkId}>
                    <td className="cartera-ola__hist-date">{iso.slice(0, 10).replace(/-/g, '/')}</td>
                    <td>{iso.slice(11, 19)}</td>
                    <td className="cartera-ola__hist-move">
                      <strong>Cobro a {h.cliente}</strong>
                      <p>{h.concepto}{h.pagadoAt ? ` · pagado ${new Date(h.pagadoAt).toISOString().slice(0, 10).replace(/-/g, '/')}` : ''}</p>
                    </td>
                    <td className="cartera-ola__hist-amt">{cop(h.montoCop)} COP</td>
                    <td>
                      <span className={`cartera-ola__chip ${chip.cls}`}>
                        <span className="cartera-ola__chip-dot" aria-hidden />
                        {chip.txt}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {(data.historial || []).length === 0 && <tr><td colSpan={5} className="cartera-ola__td-empty">Aun no hay cobros.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <p className="cartera-ola__foot">Tambien puedes crear cobros hablandole al Oraculo: &quot;cobrale $2.300.000 a Acme por la factura 4512&quot;. El boton WhatsApp abre TU WhatsApp con el recordatorio listo — tu decides a quien enviarlo.</p>
    </div>
  );
}
